import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StrategicPlanning = ({ selectedPeriod }) => {
  const [activeSection, setActiveSection] = useState('forecasting');

  const forecastData = [
    { month: 'Jul', projected: 4200000, capacity: 4800000, current: 3850000 },
    { month: 'Aug', projected: 4500000, capacity: 5000000, current: 4100000 },
    { month: 'Sep', projected: 4800000, capacity: 5200000, current: 0 },
    { month: 'Oct', projected: 5000000, capacity: 5400000, current: 0 },
    { month: 'Nov', projected: 5200000, capacity: 5600000, current: 0 },
    { month: 'Dec', projected: 5500000, capacity: 5800000, current: 0 }
  ];

  const capacityData = [
    { department: 'Corporate Law', current: 85, optimal: 90, max: 100 },
    { department: 'Litigation', current: 92, optimal: 88, max: 100 },
    { department: 'Real Estate', current: 78, optimal: 85, max: 100 },
    { department: 'Family Law', current: 88, optimal: 85, max: 100 },
    { department: 'Criminal Law', current: 82, optimal: 80, max: 100 }
  ];

  const growthOpportunities = [
    {
      id: 1,
      opportunity: 'Corporate M&A Expansion',
      potential: 'High',
      investment: 2500000,
      timeframe: '6-12 months',
      roi: '180%',
      priority: 'high',
      description: 'Expand corporate mergers and acquisitions practice area'
    },
    {
      id: 2,
      opportunity: 'Digital Legal Services',
      potential: 'High',
      investment: 1800000,
      timeframe: '3-6 months',
      roi: '220%',
      priority: 'high',
      description: 'Launch online legal consultation and document services'
    },
    {
      id: 3,
      opportunity: 'International Practice',
      potential: 'Medium',
      investment: 3200000,
      timeframe: '12-18 months',
      roi: '150%',
      priority: 'medium',
      description: 'Establish international law practice for cross-border transactions'
    },
    {
      id: 4,
      opportunity: 'Technology Upgrade',
      potential: 'Medium',
      investment: 1200000,
      timeframe: '3-4 months',
      roi: '95%',
      priority: 'medium',
      description: 'Implement AI-powered legal research and case management'
    },
    {
      id: 5,
      opportunity: 'Satellite Office',
      potential: 'Low',
      investment: 2800000,
      timeframe: '9-12 months',
      roi: '85%',
      priority: 'low',
      description: 'Open satellite office in emerging business district'
    }
  ];

  const strategicGoals = [
    {
      goal: 'Revenue Growth',
      target: '25% increase',
      current: '18% achieved',
      status: 'on-track',
      deadline: 'Dec 2024'
    },
    {
      goal: 'Client Satisfaction',
      target: '95% rating',
      current: '94.7% achieved',
      status: 'on-track',
      deadline: 'Ongoing'
    },
    {
      goal: 'Practice Expansion',
      target: '3 new areas',
      current: '1 completed',
      status: 'behind',
      deadline: 'Q4 2024'
    },
    {
      goal: 'Technology Integration',
      target: '100% digital',
      current: '75% completed',
      status: 'on-track',
      deadline: 'Q3 2024'
    }
  ];

  const sections = [
    { id: 'forecasting', label: 'Case Load Forecasting', icon: 'TrendingUp' },
    { id: 'capacity', label: 'Capacity Planning', icon: 'Users' },
    { id: 'opportunities', label: 'Growth Opportunities', icon: 'Target' },
    { id: 'goals', label: 'Strategic Goals', icon: 'CheckCircle' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning-600';
      case 'low': return 'text-success';
      default: return 'text-text-secondary';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error-50 border-error-200';
      case 'medium': return 'bg-warning-50 border-warning-200';
      case 'low': return 'bg-success-50 border-success-200';
      default: return 'bg-secondary-50 border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return 'text-success';
      case 'behind': return 'text-error';
      case 'ahead': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  const getCapacityColor = (current, optimal) => {
    if (current > optimal + 5) return 'text-error';
    if (current < optimal - 5) return 'text-warning-600';
    return 'text-success';
  };

  const formatCurrency = (value) => {
    return `KES ${(value / 1000000).toFixed(1)}M`;
  };

  return (
    <div className="bg-surface rounded-lg shadow-card border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Strategic Planning</h2>
          <button className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth">
            <Icon name="Download" size={16} className="text-text-secondary" />
          </button>
        </div>

        {/* Section Navigation */}
        <div className="flex flex-wrap gap-2">
          {sections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                activeSection === section?.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
              }`}
            >
              <Icon name={section?.icon} size={16} />
              <span className="hidden sm:inline">{section?.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Case Load Forecasting */}
        {activeSection === 'forecasting' && (
          <div>
            <div className="mb-6">
              <h3 className="text-md font-semibold text-text-primary mb-2">Revenue and Capacity Projections</h3>
              <p className="text-sm text-text-secondary">
                6-month forecast based on current trends and historical data
              </p>
            </div>

            <div className="h-80 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={forecastData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                  <Tooltip 
                    formatter={(value) => [`${formatCurrency(value)}`, '']}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="projected" 
                    stroke="#6366F1" 
                    strokeWidth={2}
                    dot={{ fill: '#6366F1' }}
                    name="Projected Revenue"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="capacity" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#10B981' }}
                    name="Capacity Limit"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="current" 
                    stroke="#94A3B8" 
                    strokeWidth={2}
                    dot={{ fill: '#94A3B8' }}
                    name="Current Performance"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-secondary-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-2">Projected Growth</h4>
                <p className="text-lg font-bold text-success">+31%</p>
                <p className="text-xs text-text-secondary">Next 6 months</p>
              </div>
              <div className="bg-secondary-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-2">Capacity Utilization</h4>
                <p className="text-lg font-bold text-warning-600">87%</p>
                <p className="text-xs text-text-secondary">Current average</p>
              </div>
              <div className="bg-secondary-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-2">Revenue Target</h4>
                <p className="text-lg font-bold text-primary">KES 29.2M</p>
                <p className="text-xs text-text-secondary">6-month target</p>
              </div>
            </div>
          </div>
        )}

        {/* Capacity Planning */}
        {activeSection === 'capacity' && (
          <div>
            <div className="mb-6">
              <h3 className="text-md font-semibold text-text-primary mb-2">Department Capacity Analysis</h3>
              <p className="text-sm text-text-secondary">
                Resource utilization by practice area
              </p>
            </div>

            <div className="space-y-4">
              {capacityData?.map((dept, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-text-primary">{dept?.department}</h4>
                    <span className={`text-sm font-medium ${getCapacityColor(dept?.current, dept?.optimal)}`}>
                      {dept?.current}% utilized
                    </span>
                  </div>
                  
                  <div className="relative">
                    <div className="w-full bg-secondary-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${
                          dept?.current > dept?.optimal + 5 ? 'bg-error' :
                          dept?.current < dept?.optimal - 5 ? 'bg-warning-500' : 'bg-success'
                        }`}
                        style={{ width: `${dept?.current}%` }}
                      ></div>
                    </div>
                    <div 
                      className="absolute top-0 w-0.5 h-3 bg-primary"
                      style={{ left: `${dept?.optimal}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
                    <span>Current: {dept?.current}%</span>
                    <span>Optimal: {dept?.optimal}%</span>
                    <span>Max: {dept?.max}%</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-primary-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertCircle" size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-primary">Capacity Insights</h4>
              </div>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Litigation department is over-utilized - consider hiring additional associates</li>
                <li>• Real Estate practice has capacity for 7% more cases</li>
                <li>• Corporate Law is near optimal utilization levels</li>
              </ul>
            </div>
          </div>
        )}

        {/* Growth Opportunities */}
        {activeSection === 'opportunities' && (
          <div>
            <div className="mb-6">
              <h3 className="text-md font-semibold text-text-primary mb-2">Strategic Growth Opportunities</h3>
              <p className="text-sm text-text-secondary">
                Ranked by potential ROI and strategic alignment
              </p>
            </div>

            <div className="space-y-4">
              {growthOpportunities?.map((opportunity) => (
                <div 
                  key={opportunity?.id} 
                  className={`p-4 rounded-lg border-2 ${getPriorityBg(opportunity?.priority)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-semibold text-text-primary">{opportunity?.opportunity}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          opportunity?.priority === 'high' ? 'bg-error-100 text-error-700' :
                          opportunity?.priority === 'medium'? 'bg-warning-100 text-warning-700' : 'bg-success-100 text-success-700'
                        }`}>
                          {opportunity?.priority} priority
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary mb-2">{opportunity?.description}</p>
                    </div>
                    <Icon name="Target" size={16} className={getPriorityColor(opportunity?.priority)} />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Investment:</span>
                      <p className="font-semibold text-text-primary">{formatCurrency(opportunity?.investment)}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Timeframe:</span>
                      <p className="font-semibold text-text-primary">{opportunity?.timeframe}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Potential:</span>
                      <p className="font-semibold text-text-primary">{opportunity?.potential}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">ROI:</span>
                      <p className="font-semibold text-success">{opportunity?.roi}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strategic Goals */}
        {activeSection === 'goals' && (
          <div>
            <div className="mb-6">
              <h3 className="text-md font-semibold text-text-primary mb-2">Strategic Goals Tracking</h3>
              <p className="text-sm text-text-secondary">
                Progress on key strategic initiatives
              </p>
            </div>

            <div className="space-y-4">
              {strategicGoals?.map((goal, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-text-primary">{goal?.goal}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        goal?.status === 'on-track' ? 'bg-success-100 text-success-700' :
                        goal?.status === 'behind'? 'bg-error-100 text-error-700' : 'bg-primary-100 text-primary-700'
                      }`}>
                        {goal?.status === 'on-track' ? 'On Track' : 
                         goal?.status === 'behind' ? 'Behind' : 'Ahead'}
                      </span>
                      <Icon 
                        name={goal?.status === 'on-track' ? 'CheckCircle' : 
                              goal?.status === 'behind' ? 'AlertCircle' : 'TrendingUp'} 
                        size={16} 
                        className={getStatusColor(goal?.status)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Target:</span>
                      <p className="font-semibold text-text-primary">{goal?.target}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Current:</span>
                      <p className="font-semibold text-text-primary">{goal?.current}</p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Deadline:</span>
                      <p className="font-semibold text-text-primary">{goal?.deadline}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-success-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <h4 className="text-sm font-semibold text-success">Overall Progress</h4>
              </div>
              <p className="text-sm text-success-700">
                3 of 4 strategic goals are on track. Practice expansion initiative requires additional focus to meet Q4 deadline.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategicPlanning;